import { useEffect, useMemo, useState } from 'react';
import { fmtMMSS, totalTargetSec, MIN_REAL_CUT_MIN } from '@busy/core';
import {
  createClientPhoto,
  createVisit,
  useClients,
  useLatestClientPhotoUrls,
  useRecentVisits,
  type ClientRow,
  type CutQuality,
  type PaymentMethod,
  type VisitPhaseTime,
} from '@busy/api';
import { Avatar, ModalCloseButton, useToast } from '@busy/ui';
import { LONG_CUT_RATIO, useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { BeforeAfterSummarySection } from './BeforeAfterSummarySection';

// Post-cut summary: verdict + per-phase breakdown + addon recap + price + "before they
// leave the chair" coaching plays + save-to-client chip (walk-ins) + save visit.
export function PostCutSummary() {
  const cutType = useCutTimer((s) => s.cutType);
  const client = useCutTimer((s) => s.client);
  const phaseElapsedSec = useCutTimer((s) => s.phaseElapsedSec);
  const addedAddons = useCutTimer((s) => s.addedAddons);
  const startedAtIso = useCutTimer((s) => s.startedAtIso);
  const endedAtIso = useCutTimer((s) => s.endedAtIso);
  const closeModal = useCutTimer((s) => s.closeModal);
  const resetAll = useCutTimer((s) => s.resetAll);
  const attachClient = useCutTimer((s) => s.attachClient);
  const useTargetTime = useCutTimer((s) => s.useTargetTimeAsElapsed);
  const beforePhotoBlob = useCutTimer((s) => s.beforePhotoBlob);
  const afterPhotoBlob = useCutTimer((s) => s.afterPhotoBlob);
  const transformationGenerated = useCutTimer((s) => s.transformationGenerated);
  const staleAutoEnded = useCutTimer((s) => s.staleAutoEnded);
  // Walk-in vs appointment picked at check-in (migration 0072 capture
  // point moved earlier in the flow). PostCutSummary still surfaces the
  // ClientSourceButton as a final correction step — initialized from
  // this value so the check-in answer auto-flows through.
  const pendingWasAppointment = useCutTimer((s) => s.pendingWasAppointment);
  // Quality rating picked at the RATING step (post-endCut, pre-after-
  // photo). Same idea as pendingWasAppointment but for the cut quality
  // self-rating — captured while judgment is freshest, with this
  // screen as the final correction step.
  const pendingQuality = useCutTimer((s) => s.pendingQuality);
  const toast = useToast();
  const t = useT();

  const defaultPrice = useMemo(() => {
    const base = cutType?.priceCents ?? 0;
    const addons = addedAddons.reduce((sum, a) => sum + a.priceCents, 0);
    return ((base + addons) / 100).toFixed(0);
  }, [cutType, addedAddons]);

  const [price, setPrice] = useState(defaultPrice);
  // Optional tip in dollars (string for input control). Defaults to empty
  // so the barber explicitly opts in rather than seeing "$0" implying a
  // zero-tip was recorded. Stored separately from price_cents on save
  // (column tip_cents from migration 0065) so it never inflates the
  // revenue achievement counter or muddies the price-variance audit.
  const [tip, setTip] = useState('');
  // Optional payment method (migration 0071). Captured at save time so
  // the barber can reconcile against 1099-Ks at year-end. Default
  // seeded below from the user's most-frequent recent payment method
  // — most barbers have a heavy preference (Venmo / cash / card), so
  // pre-lighting their typical chip saves a tap on the common case.
  // Tap-to-deselect still works, and the seed only fires when the
  // current value is null (won't overwrite an explicit pick).
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  // Recent-visits feed for the payment-method default. Cached by the
  // shared visits invalidation key so this query is usually a hit
  // (RecentVisits is mounted by Dashboard + Coaching insights already).
  const recentVisits = useRecentVisits(90);
  // Seed the payment chip once visits load. Only fires when the chip
  // is still null AND we have enough sample (≥3 tagged visits) — small
  // samples can mislead. If the barber manually deselects later we
  // don't re-seed (dep on visits.data only; their toggle doesn't re-
  // trigger the effect).
  useEffect(() => {
    if (paymentMethod !== null) return;
    const seed = computeMostFrequentPaymentMethod(recentVisits.data ?? null);
    if (seed) setPaymentMethod(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentVisits.data]);
  // Optional walk-in vs appointment flag (migration 0072). Drives the
  // 7-day forecast's confidence bands + walk-in % reports. Seeded from
  // the check-in chip the barber tapped — defaulting to null when
  // skipped (UI still surfaces the picker as a final correction step).
  const [wasAppointment, setWasAppointment] = useState<boolean | null>(pendingWasAppointment);
  // Optional self-rating of how this cut went (migration 0075). 1 = off,
  // 2 = solid, 3 = nailed it. Seeded from the rating step (post-endCut,
  // pre-after-photo) where the barber's judgment is freshest. Null
  // means they skipped both this AND the rating step — honest signal
  // capture without forcing an answer.
  const [quality, setQuality] = useState<CutQuality | null>(pendingQuality);
  // Pull pause + skip stats from the cut timer store at save time. These
  // are session-wide accumulators maintained by pause/resume/switchToPhase
  // (see store/cutTimer.ts). Persisted to visits.paused_seconds /
  // pause_count / phases_skipped (migration 0075).
  const sessionPausedMs = useCutTimer((s) => s.sessionPausedMs);
  const sessionPauseCount = useCutTimer((s) => s.sessionPauseCount);
  const sessionPhasesSkipped = useCutTimer((s) => s.sessionPhasesSkipped);
  const [saving, setSaving] = useState(false);
  const [showClientPicker, setShowClientPicker] = useState(false);
  // Time-by-phase is dense; default collapsed so the actionable parts of the
  // summary (coaching plays + price + save) sit above the fold.
  const [phasesExpanded, setPhasesExpanded] = useState(false);
  // 2-tap discard guard: first tap arms the button (turns red, copy changes),
  // second tap actually discards. Replaces the native confirm() which was
  // unreliable inside the modal on iOS PWA.
  const [discardArmed, setDiscardArmed] = useState(false);

  if (!cutType) return null;

  const totalElapsed = phaseElapsedSec.reduce((a, b) => a + (b ?? 0), 0);
  const totalTarget = totalTargetSec(cutType.phases as never[]);
  const diff = totalElapsed - totalTarget;
  const absMin = Math.abs(diff) / 60;
  const isLongCut = totalTarget > 0 && totalElapsed > totalTarget * LONG_CUT_RATIO;
  const verdictKind: 'target' | 'under' | 'over' =
    Math.abs(diff) <= 30 ? 'target' : diff < 0 ? 'under' : 'over';
  const verdict =
    verdictKind === 'target'
      ? t('cut.summary.verdict.target')
      : verdictKind === 'under'
      ? t('cut.summary.verdict.under', { min: absMin.toFixed(1) })
      : t('cut.summary.verdict.over', { min: absMin.toFixed(1) });
  const detail = t('cut.summary.detail', {
    elapsed: fmtMMSS(totalElapsed),
    target: fmtMMSS(totalTarget),
  });

  const isWalkin = client?.kind === 'walkin';
  const cutName = cutType.nameKey ? t(cutType.nameKey) : cutType.name;

  async function handleSave() {
    const priceNum = parseFloat(price);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      toast(t('cut.summary.invalidPrice'), 'error');
      return;
    }
    // Tip is optional. Empty → zero. Negative or non-numeric → reject
    // with a toast so the barber sees what's wrong rather than the DB
    // CHECK constraint blowing up at insert. Cap at $500 (mirrors the
    // DB CHECK from 0065).
    const trimmedTip = tip.trim();
    let tipNum = 0;
    if (trimmedTip.length > 0) {
      tipNum = parseFloat(trimmedTip);
      if (Number.isNaN(tipNum) || tipNum < 0) {
        toast(t('cut.summary.invalidTip'), 'error');
        return;
      }
      if (tipNum > 500) {
        toast(t('cut.summary.tipTooLarge'), 'error');
        return;
      }
    }
    // Confirm any deviation from the default ticket — protects against typos
    // and gives the barber a beat to catch a wrong number before it lands in
    // their revenue rollups.
    const defaultNum = parseFloat(defaultPrice);
    const priceChanged = !Number.isNaN(defaultNum) && Math.abs(priceNum - defaultNum) >= 0.01;
    if (priceChanged) {
      const ok = confirm(
        t('cut.summary.priceChangeConfirm', {
          newPrice: priceNum.toFixed(2),
          defaultPrice: defaultNum.toFixed(2),
        }),
      );
      if (!ok) return;
    }
    setSaving(true);
    // Persist canonical English names — keeps EOD and reports consistent across
    // language toggles. Display-side translation kicks in via nameKey.
    const cutTypeName =
      cutType!.name + (addedAddons.length > 0 ? ` + ${addedAddons.map((a) => a.name).join(', ')}` : '');
    const result = await createVisit({
      client_id: client?.kind === 'existing' ? client.id : null,
      cut_type: cutTypeName,
      price_cents: Math.round(priceNum * 100),
      tip_cents: Math.round(tipNum * 100),
      payment_method: paymentMethod,
      was_appointment: wasAppointment,
      // Cut-timer signals (migration 0075). quality is barber-tagged;
      // the rest are auto-captured during the cut by the timer store.
      quality,
      phase_times: buildPhaseTimes(cutType!.phases, phaseElapsedSec),
      paused_seconds: Math.round(sessionPausedMs / 1000),
      pause_count: sessionPauseCount,
      phases_skipped: sessionPhasesSkipped,
      // Audit trail — persists what the cut "should have" been so any
      // discount/upcharge is measurable. Defaults from the cut type + add-ons
      // and is computed independent of the barber's edits.
      default_price_cents: Math.round((parseFloat(defaultPrice) || 0) * 100),
      // Structured add-on list — drives the EOD per-add-on breakdown +
      // any future "you sold X beard trims this month" rollups.
      addons: addedAddons.map((a) => ({
        id: a.id,
        name: a.name,
        priceCents: a.priceCents,
      })),
      duration_min: Math.round(totalElapsed / 60),
      started_at: startedAtIso ?? new Date().toISOString(),
      ended_at: endedAtIso ?? new Date().toISOString(),
      // Drives the 'transformations' achievement kind — counts when the
      // barber actually generated a transformation video on the reveal
      // screen (mode='reveal'). Skipping reveal via the "Skip & wrap-up"
      // bypass leaves transformationGenerated=false, so no credit is
      // awarded — that prevents farming the badge by capturing photos but
      // never going through the reveal flow.
      had_before_after: transformationGenerated,
    });
    // Upload before/after photos to the client's gallery — runs after the
    // visit insert succeeds and only when we have a real client id (the
    // walk-in path is no longer reachable for new cuts; this guard covers
    // any in-flight cut that was started under the old code path).
    const targetClientId = client?.kind === 'existing' ? client.id : null;
    if (!result.error && targetClientId) {
      const uploads: Array<Promise<unknown>> = [];
      if (beforePhotoBlob) {
        const file = new File([beforePhotoBlob], `before-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        uploads.push(createClientPhoto(targetClientId, file));
      }
      if (afterPhotoBlob) {
        const file = new File([afterPhotoBlob], `after-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        uploads.push(createClientPhoto(targetClientId, file));
      }
      // Best-effort — don't block the success toast on photo upload. If
      // either upload fails we still saved the visit, and the photos
      // just won't appear in the gallery for that cut.
      if (uploads.length > 0) await Promise.allSettled(uploads);
    }
    setSaving(false);
    if (result.error) {
      toast(t('cut.summary.saveError', { message: result.error.message }), 'error');
    } else {
      toast(t('cut.summary.visitLogged'), 'success');
      resetAll();
    }
  }

  function handleDiscard() {
    if (!discardArmed) {
      setDiscardArmed(true);
      // Auto-disarm after 4s if the barber doesn't follow through — guards
      // against accidentally leaving the button armed on a backgrounded tab.
      window.setTimeout(() => setDiscardArmed(false), 4000);
      return;
    }
    // Route through closeModal so the abandon event gets logged via the
    // store's 'summary' branch. closeModal in summary mode does the same
    // teardown resetAll did before (revoke photo URLs + freshActiveState).
    closeModal();
  }

  return (
    <div className="flex flex-col max-h-[90dvh]">
      {/* Header — pinned above the scroll region. */}
      <div className="shrink-0 px-6 pt-7 pb-3 relative border-b border-line">
        <ModalCloseButton onClose={closeModal} />
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-500 inline-flex items-center gap-1.5 mb-1.5">
          <BoltIcon className="w-3 h-3" />
          {t('cut.summary.eyebrow')}
        </div>
        <h2 className="font-display text-2xl">{cutName}</h2>
        {client?.kind === 'existing' && (
          <p className="text-sm text-ink-500 mt-0.5">
            {t('cut.summary.forClient', {
              first: client.firstName,
              last: client.lastName,
            })}
          </p>
        )}
        {isWalkin && (
          <p className="text-sm text-ink-500 mt-0.5">{t('cut.summary.walkinNotAttributed')}</p>
        )}
      </div>

      {/* Scrollable body. min-h-0 lets the flex child actually shrink in
          Safari; without it the body grows to fit content and the footer
          floats off-screen. */}
      <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
        {/* Before they leave the chair — coaching plays. Pulled to the top
            so the actionable list (photo / review / rebook) sits above the
            fold while the client is still in the chair. */}
        <div className="rounded-xl border border-gold-300/60 dark:border-gold-500/30 bg-gold-50/60 dark:bg-gold-500/10 p-4">
          <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-navy-900/80 dark:text-gold-300 inline-flex items-center gap-1.5 mb-3">
            <LightbulbIcon className="w-3 h-3" />
            {t('cut.summary.beforeLeave')}
          </div>
          <ul className="space-y-2.5 text-sm">
            <PlayItem
              icon={<CameraIcon className="w-4 h-4" />}
              text={t('cut.summary.play.photo')}
              note={t('cut.summary.play.photoNote')}
            />
            <PlayItem
              icon={<StarIcon className="w-4 h-4" />}
              text={t('cut.summary.play.review')}
            />
            <PlayItem
              icon={<ClockIcon className="w-4 h-4" />}
              text={t('cut.summary.play.rebook')}
            />
          </ul>
        </div>

        {/* Before/After photo capture + composite. */}
        {client?.kind === 'existing' && <BeforeAfterSummarySection />}

        {/* Stale-timer auto-ended notice */}
        {staleAutoEnded && (
          <div className="rounded-xl border border-orange-200 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/15 p-3 text-sm space-y-1">
            <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-orange-700 dark:text-orange-300 inline-flex items-center gap-1.5">
              <AlertIcon className="w-3 h-3" />
              {t('cut.summary.autoEnded.eyebrow')}
            </div>
            <p className="text-orange-900 dark:text-orange-200 leading-snug">
              {t('cut.summary.autoEnded.body')}
            </p>
          </div>
        )}

        {/* Long-cut confirmation */}
        {isLongCut && !staleAutoEnded && (
          <div className="rounded-xl border border-orange-200 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/15 p-3 text-sm space-y-2">
            <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-orange-700 dark:text-orange-300 inline-flex items-center gap-1.5">
              <AlertIcon className="w-3 h-3" />
              {t('cut.summary.longCut.eyebrow')}
            </div>
            <p className="text-orange-900 dark:text-orange-200 leading-snug">
              {t('cut.summary.longCut.body', {
                min: absMin.toFixed(1),
                target: fmtMMSS(totalTarget),
              })}
            </p>
            <button
              type="button"
              onClick={() => {
                useTargetTime();
                toast(t('cut.summary.toast.targetTime'), 'info');
              }}
              className="text-xs font-bold text-orange-700 dark:text-orange-300 underline-offset-2 hover:underline"
            >
              {t('cut.summary.longCut.useTarget')}
            </button>
          </div>
        )}

        <div
          className={`flex items-start gap-3 rounded-xl p-4 ${
            verdictKind === 'target'
              ? 'bg-green-50 dark:bg-green-600/15 border border-green-100 dark:border-green-600/40'
              : verdictKind === 'under'
              ? 'bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-100 dark:border-emerald-500/40'
              : 'bg-red-50 dark:bg-red-600/15 border border-red-100 dark:border-red-600/40'
          }`}
        >
          <div
            className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
              verdictKind === 'over' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
            }`}
          >
            {verdictKind === 'over' ? <TrendUpIcon className="w-4 h-4" /> : <CheckIcon className="w-4 h-4" />}
          </div>
          <div>
            <div className="font-display text-lg leading-tight">{verdict}</div>
            <div className="text-sm text-ink-500 mt-0.5">{detail}</div>
          </div>
        </div>

        {/* Anti-gaming footnote — fires when the cut ran for less than the
            achievement-counting threshold (5 min). The visit will still
            save and show on dashboards / EOD / client gallery normally,
            but it won't bump achievement progress or the streak. Tells
            the barber up front so they don't wonder why their badge
            didn't move. */}
        {totalElapsed < MIN_REAL_CUT_MIN * 60 && (
          <div className="rounded-xl border border-line bg-bg-alt p-3 text-[12px] text-ink-700 leading-snug">
            {t('cut.summary.subThresholdNote', { min: MIN_REAL_CUT_MIN })}
          </div>
        )}

        {/* Time-by-phase — collapsed by default. The summary is dense and
            most barbers only want it occasionally; pulling it below the fold
            keeps the actionable section (price + save) closer to the eye. */}
        <div className="rounded-xl border border-line bg-bg-alt overflow-hidden">
          <button
            type="button"
            onClick={() => setPhasesExpanded((v) => !v)}
            aria-expanded={phasesExpanded}
            className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-bg-alt/80 transition-colors"
          >
            <span className="text-[11px] tracking-[0.12em] uppercase font-bold text-ink-500">
              {t('cut.summary.timeByPhase')}
            </span>
            <ChevronIcon
              className={`w-4 h-4 text-ink-500 transition-transform ${
                phasesExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
          {phasesExpanded && (
            <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-line">
              {cutType.phases.map((p, i) => {
                const elapsed = phaseElapsedSec[i] ?? 0;
                const skipped = elapsed === 0;
                const phaseDiff = elapsed - p.sec;
                const cls =
                  Math.abs(phaseDiff) <= 15
                    ? 'text-green-600'
                    : phaseDiff < 0
                    ? 'text-emerald-600'
                    : 'text-red-600';
                return (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-ink-700">
                      {i + 1}. {p.nameKey ? t(p.nameKey) : p.name}
                    </span>
                    {skipped ? (
                      <span className="text-ink-500 italic">{t('cut.summary.skipped')}</span>
                    ) : (
                      <div className="flex items-center gap-2 tabular-nums">
                        <span className="text-ink-700">
                          {fmtMMSS(elapsed)} / {fmtMMSS(p.sec)}
                        </span>
                        <span className={`font-bold text-xs ${cls}`}>
                          {phaseDiff > 0 ? '+' : phaseDiff < 0 ? '−' : ''}
                          {fmtMMSS(Math.abs(phaseDiff))}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {addedAddons.length > 0 && (
          <div className="rounded-xl border border-green-200 dark:border-green-600/40 bg-green-50 dark:bg-green-600/15 p-3">
            <div className="text-[11px] tracking-[0.12em] uppercase font-bold text-green-700 dark:text-green-300 mb-1">
              {t('cut.summary.addonsSold')}
            </div>
            <ul className="space-y-0.5 text-sm">
              {addedAddons.map((a) => (
                <li key={a.id} className="flex justify-between text-green-800 dark:text-green-200">
                  <span>+ {a.nameKey ? t(a.nameKey) : a.name}</span>
                  <span className="font-bold tabular-nums">${(a.priceCents / 100).toFixed(0)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Save-to-client chip — walk-ins only */}
        {isWalkin && !showClientPicker && (
          <button
            type="button"
            onClick={() => setShowClientPicker(true)}
            className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-dashed border-line text-ink-700 font-semibold hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
          >
            <UserPlusIcon className="w-4 h-4" />
            {t('cut.summary.saveToClient')}
          </button>
        )}
        {isWalkin && showClientPicker && (
          <SaveToClientPicker
            onPick={(c) => {
              attachClient({
                kind: 'existing',
                id: c.id,
                firstName: c.first_name,
                lastName: c.last_name,
                phone: c.phone,
              });
              setShowClientPicker(false);
              const fullName = `${c.first_name} ${c.last_name}`.trim();
              toast(
                t('cut.summary.savedTo', {
                  name: fullName || t('cut.summary.savedToFallback'),
                }),
                'success',
              );
            }}
            onCancel={() => setShowClientPicker(false)}
          />
        )}
        {client?.kind === 'existing' && (
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-600/15 border border-green-200 dark:border-green-600/40 text-green-800 dark:text-green-200 text-sm font-semibold">
            <CheckIcon className="w-4 h-4" />
            <span>
              {t('cut.summary.savedTo', {
                name: `${client.firstName} ${client.lastName}`.trim() || t('cut.summary.savedToFallback'),
              })}
            </span>
          </div>
        )}

        {/* Ticket — price, tip, total. Tip is optional (empty = no tip
            recorded, not $0 tip). Total is read-only and reflects what
            the client actually paid; the DB stores price + tip as two
            columns so future "tip earnings" rollups stay separable from
            cut revenue + the revenue achievement counter can't be
            inflated by tip variance. */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-ink-500 mb-1">
              {t('cut.summary.priceLabel')}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 font-bold pointer-events-none select-none">$</span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full h-12 pl-7 pr-3 rounded-lg border border-line bg-surface text-ink-900 text-lg font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-500 mb-1">
              {t('cut.summary.tipLabel')}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 font-bold pointer-events-none select-none">$</span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={tip}
                placeholder={t('cut.summary.tipPlaceholder')}
                onChange={(e) => setTip(e.target.value)}
                className="w-full h-12 pl-7 pr-3 rounded-lg border border-line bg-surface text-ink-900 text-lg font-bold tabular-nums placeholder:text-ink-500/60 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
              />
            </div>
            {/* Quick-pick chips — 15/20/25 percent of the current price.
                Tap fills in the matching dollar amount. Tip percentages
                are the universal service-industry vocabulary; rendering
                exact dollar values next to each chip removes the mental
                math. Custom remains the input field. */}
            {(() => {
              const priceNumForChips = parseFloat(price);
              if (Number.isNaN(priceNumForChips) || priceNumForChips <= 0) return null;
              const percents = [15, 20, 25];
              return (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  {percents.map((p) => {
                    const dollars = Math.round((priceNumForChips * p) / 100);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setTip(String(dollars))}
                        className="h-7 px-2.5 rounded-full bg-bg-alt text-ink-700 text-[11px] font-bold hover:bg-bg-alt/70 transition-colors tabular-nums"
                      >
                        {t('cut.summary.tipChip', { pct: p, dollars })}
                      </button>
                    );
                  })}
                  {tip && (
                    <button
                      type="button"
                      onClick={() => setTip('')}
                      className="h-7 px-2.5 rounded-full text-ink-500 text-[11px] font-bold hover:bg-bg-alt transition-colors"
                    >
                      {t('cut.summary.tipClear')}
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
          {/* Total — sum of price + tip, displayed read-only. Renders
              even when tip is empty so the row order reads price → tip
              → total without a layout shift the moment a tip is typed. */}
          {(() => {
            const priceNumForTotal = parseFloat(price);
            const tipNumForTotal = tip.trim() ? parseFloat(tip) : 0;
            const safePrice = Number.isFinite(priceNumForTotal) ? priceNumForTotal : 0;
            const safeTip = Number.isFinite(tipNumForTotal) && tipNumForTotal >= 0 ? tipNumForTotal : 0;
            const total = safePrice + safeTip;
            return (
              <div className="flex items-center justify-between border-t border-line pt-3 mt-1">
                <span className="text-[11px] font-extrabold tracking-[0.14em] uppercase text-ink-500">
                  {t('cut.summary.total')}
                </span>
                <span className="font-display text-2xl text-navy-900 dark:text-gold-500 tabular-nums">
                  ${total.toFixed(total % 1 === 0 ? 0 : 2)}
                </span>
              </div>
            );
          })()}
          {/* Payment method picker — optional. Captures HOW the cut got
              paid for so the barber can reconcile each 1099-K at tax
              time (Square / Venmo / Cash App report independently to
              the IRS). Six chip options; tapping again deselects. No
              default — empty state lands as "Unspecified" in recaps,
              which is fine for back-compat with the existing pre-0071
              flow. */}
          <div className="pt-1">
            <label className="block text-xs font-semibold text-ink-500 mb-1.5">
              {t('cut.summary.paymentLabel')}
            </label>
            <PaymentMethodChips value={paymentMethod} onChange={setPaymentMethod} />
          </div>
          {/* Client source — single button. Tapping opens a small
              inline menu with Walk-in / Appointment / Clear. Drives
              the walk-in vs appointment % on capacity + recaps. */}
          <div className="pt-1">
            <ClientSourceButton value={wasAppointment} onChange={setWasAppointment} />
          </div>
          {/* How'd that cut go? — barber self-rating (migration 0075).
              Three faces: off / solid / nailed. Tap to set, tap the
              same one to clear. Cleanest "quality signal" the app has
              — pairs with tip + rebook patterns for correlation
              analysis ("your nailed-it cuts tip 2× more"). */}
          <div className="pt-1">
            <label className="block text-xs font-semibold text-ink-500 mb-1.5">
              {t('cut.summary.qualityLabel')}
            </label>
            <QualityPicker value={quality} onChange={setQuality} />
          </div>
        </div>
      </div>

      {/* Sticky footer — Save / Discard always reachable regardless of how
          long the body grows. Discard uses 2-tap arming (first tap reddens
          the button, second tap fires) to replace the unreliable native
          confirm() dialog inside modals on iOS PWA. */}
      <div className="shrink-0 border-t border-line bg-surface px-6 py-3 flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 h-12 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
        >
          {saving ? t('cut.summary.saving') : t('cut.summary.saveVisit')}
        </button>
        <button
          type="button"
          onClick={handleDiscard}
          className={`px-5 h-12 rounded-lg font-semibold transition-colors ${
            discardArmed
              ? 'border-2 border-red-500/70 bg-red-500/10 text-red-700 dark:text-red-300'
              : 'border border-line bg-surface text-ink-700'
          }`}
        >
          {discardArmed ? t('cut.summary.discardConfirmTap') : t('cut.summary.discard')}
        </button>
      </div>
    </div>
  );
}

/** Return the most-frequent payment_method across recent visits, or
 *  null if the sample's too small to be meaningful. Threshold of 3
 *  tagged visits balances "I have a clear preference" (e.g. 5 Venmo
 *  cuts last week) against accidentally pre-lighting whatever the
 *  barber happened to tag once. The pre-fill saves a tap on the
 *  common case (barbers with a heavy default payment method); below
 *  the threshold we stay null so we never anchor wrong. */
function computeMostFrequentPaymentMethod(
  visits: { payment_method: PaymentMethod | null }[] | null,
): PaymentMethod | null {
  if (!visits || visits.length === 0) return null;
  const counts = new Map<PaymentMethod, number>();
  let total = 0;
  for (const v of visits) {
    if (!v.payment_method) continue;
    counts.set(v.payment_method, (counts.get(v.payment_method) ?? 0) + 1);
    total += 1;
  }
  if (total < 3) return null;
  let best: PaymentMethod | null = null;
  let bestCount = 0;
  for (const [method, count] of counts) {
    if (count > bestCount) {
      best = method;
      bestCount = count;
    }
  }
  return best;
}

/** Compact chip picker for payment_method. Six options, tap to select,
 *  tap-again to deselect (null state). Shared across PostCutSummary
 *  (capture at save) + EditVisitSheet (backfill within 24h window). */
const PAYMENT_METHOD_OPTIONS: ReadonlyArray<PaymentMethod> = [
  'cash',
  'card',
  'venmo',
  'zelle',
  'cashapp',
  'other',
];

export function PaymentMethodChips({
  value,
  onChange,
}: {
  value: PaymentMethod | null;
  onChange: (next: PaymentMethod | null) => void;
}) {
  const t = useT();
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {PAYMENT_METHOD_OPTIONS.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(active ? null : opt)}
            aria-pressed={active}
            className={`h-9 px-3 rounded-full text-xs font-bold tabular-nums transition-colors ${
              active
                ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                : 'bg-bg-alt text-ink-700 hover:bg-bg-alt/70'
            }`}
          >
            {t(`payment.${opt}`)}
          </button>
        );
      })}
    </div>
  );
}

/** Walk-in vs Appointment chip picker. Two chips with tap-to-deselect
 *  back to null ("Unspecified" in recaps). Stored as `was_appointment`
 *  on visits (migration 0072) — boolean rather than enum so the
 *  schema stays simple; a future column could split out "rebook"
 *  (appointments locked in at end of prior visit) once we want to
 *  coach on rebooking discipline as its own behavior. */
export function ClientSourceButton({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (next: boolean | null) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);

  // Display text — current selection, or the placeholder when null.
  // Walk-in = false, Appointment = true; null = unspecified.
  const valueText =
    value === false
      ? t('visitSource.walkin')
      : value === true
        ? t('visitSource.appointment')
        : t('cut.summary.sourcePlaceholder');

  function pick(next: boolean | null) {
    onChange(next);
    setOpen(false);
  }

  return (
    <div className="relative">
      {/* Single-button trigger — labeled "Client source" with the
          current value next to it. Mirrors the pill-button styling
          the cut-complete screen uses elsewhere so it sits naturally
          alongside the payment-method chip row above. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-bg-alt text-ink-700 hover:bg-bg-alt/70 transition-colors border border-line"
      >
        <span className="text-[11px] tracking-[0.1em] uppercase font-bold text-ink-500">
          {t('cut.summary.sourceLabel')}
        </span>
        <span className={`text-xs font-bold ${
          value == null ? 'text-ink-500' : 'text-navy-900 dark:text-gold-400'
        }`}>
          {valueText}
        </span>
        <ChevronDownChip className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Inline popover — opens beneath the trigger. Click-away
          handled by an invisible backdrop sibling so the picker
          dismisses naturally without us wiring up a document-level
          listener. */}
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-30 cursor-default"
          />
          <div
            role="listbox"
            aria-label={t('cut.summary.sourceLabel')}
            className="absolute left-0 top-full mt-1.5 z-40 w-44 rounded-lg border border-line bg-surface shadow-lg overflow-hidden"
          >
            <SourceOption
              label={t('visitSource.walkin')}
              selected={value === false}
              onClick={() => pick(value === false ? null : false)}
            />
            <SourceOption
              label={t('visitSource.appointment')}
              selected={value === true}
              onClick={() => pick(value === true ? null : true)}
            />
            {value != null && (
              <SourceOption
                label={t('cut.summary.sourceClear')}
                selected={false}
                onClick={() => pick(null)}
                muted
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SourceOption({
  label,
  selected,
  muted,
  onClick,
}: {
  label: string;
  selected: boolean;
  muted?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors ${
        selected
          ? 'bg-navy-900/8 dark:bg-gold-500/15 text-navy-900 dark:text-gold-400 font-bold'
          : muted
            ? 'text-ink-500 hover:bg-bg-alt'
            : 'text-ink-900 hover:bg-bg-alt'
      }`}
    >
      {label}
    </button>
  );
}

function ChevronDownChip({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function PlayItem({
  icon,
  text,
  note,
}: {
  icon: React.ReactNode;
  text: string;
  note?: string;
}) {
  return (
    <li className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-md bg-navy-900/10 dark:bg-gold-500/15 grid place-items-center shrink-0 mt-0.5 text-navy-900 dark:text-gold-300">
        {icon}
      </div>
      <div>
        <div className="text-ink-900 leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
        {note && <div className="text-xs text-ink-500 mt-0.5">{note}</div>}
      </div>
    </li>
  );
}

// Inline client picker for the save-to-client chip. Search + list, no walk-in option
// (they're already in the walk-in path; picking would just attribute it).
function SaveToClientPicker({
  onPick,
  onCancel,
}: {
  onPick: (client: ClientRow) => void;
  onCancel: () => void;
}) {
  const clients = useClients();
  const photoUrls = useLatestClientPhotoUrls();
  const t = useT();
  const [search, setSearch] = useState('');

  const matches = useMemo(() => {
    const list = clients.data ?? [];
    if (!search.trim()) return list.slice(0, 6);
    const q = search.toLowerCase();
    const digits = q.replace(/\D/g, '');
    return list
      .filter((c) => {
        const name = `${c.first_name} ${c.last_name}`.toLowerCase();
        const phone = c.phone?.replace(/\D/g, '') ?? '';
        return name.includes(q) || (digits.length > 0 && phone.includes(digits));
      })
      .slice(0, 6);
  }, [clients.data, search]);

  return (
    <div className="rounded-xl border border-line bg-bg-alt p-3 space-y-2">
      <div className="flex items-center justify-between">
        <strong className="text-sm">{t('cut.savePicker.title')}</strong>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-ink-500 hover:text-ink-900"
        >
          {t('cut.savePicker.cancel')}
        </button>
      </div>
      <input
        type="search"
        autoFocus
        placeholder={t('cut.savePicker.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full h-10 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
      />
      {clients.loading && <div className="h-12 rounded bg-bg-alt animate-pulse" />}
      {clients.data && clients.data.length === 0 && (
        <p className="text-xs text-ink-500 text-center py-2">{t('cut.savePicker.empty')}</p>
      )}
      {matches.length > 0 && (
        <ul className="space-y-1.5 max-h-56 overflow-y-auto">
          {matches.map((c) => {
            const name = `${c.first_name} ${c.last_name}`.trim() || t('today.unnamed');
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onPick(c)}
                  className="w-full text-left rounded-lg border border-line bg-surface p-2.5 flex items-center gap-2.5 hover:border-navy-900 dark:hover:border-gold-500"
                >
                  <Avatar name={name} size="sm" photoUrl={photoUrls.data?.[c.id]} />
                  <div className="flex-1 min-w-0">
                    <strong className="text-sm truncate">{name}</strong>
                    {c.phone && <div className="text-xs text-ink-500">{c.phone}</div>}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {clients.data && clients.data.length > 0 && matches.length === 0 && (
        <p className="text-xs text-ink-500 text-center py-2">{t('cut.savePicker.noMatches')}</p>
      )}
    </div>
  );
}

// ────────── Quality picker ──────────

/** Three-button quality picker — Off today / Solid / Nailed it.
 *  Maps to smallint 1 / 2 / 3 in `visits.quality`. Tap the same
 *  button twice to clear back to unspecified (null). Icons rendered
 *  as SVG (frown / meh / smile), not emoji — house rule per
 *  feedback_no_emojis.md. */
export function QualityPicker({
  value,
  onChange,
}: {
  value: CutQuality | null;
  onChange: (next: CutQuality | null) => void;
}) {
  const t = useT();
  // Order matters — left-to-right tracks the natural mental ordering
  // (worst → best). Tones color the active state so the value reads
  // at a glance: off=red, solid=amber, nailed=emerald.
  const options: Array<{
    quality: CutQuality;
    icon: 'frown' | 'meh' | 'smile';
    labelKey: string;
    activeCls: string;
  }> = [
    { quality: 1, icon: 'frown', labelKey: 'visitQuality.off',
      activeCls: 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/40 dark:border-red-700/50 dark:text-red-300' },
    { quality: 2, icon: 'meh', labelKey: 'visitQuality.solid',
      activeCls: 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/40 dark:border-amber-700/50 dark:text-amber-300' },
    { quality: 3, icon: 'smile', labelKey: 'visitQuality.nailed',
      activeCls: 'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-900/40 dark:border-emerald-700/50 dark:text-emerald-300' },
  ];
  return (
    <div className="flex items-center gap-2">
      {options.map((opt) => {
        const active = value === opt.quality;
        return (
          <button
            key={opt.quality}
            type="button"
            onClick={() => onChange(active ? null : opt.quality)}
            aria-pressed={active}
            className={`flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg border text-xs font-bold transition-colors ${
              active
                ? opt.activeCls
                : 'border-line bg-bg-alt text-ink-700 hover:bg-bg-alt/70'
            }`}
          >
            <QualityFaceIcon kind={opt.icon} className="w-5 h-5" />
            <span>{t(opt.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Three face icons used by QualityPicker. SVG-only to match the
 *  no-emoji house rule. Drawn at 24×24 with stroke-only style so
 *  they pick up the parent text color naturally. */
function QualityFaceIcon({
  kind,
  className,
}: {
  kind: 'frown' | 'meh' | 'smile';
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
      {kind === 'smile' && <path d="M8 14s1.5 2 4 2 4-2 4-2" />}
      {kind === 'meh' && <line x1="8" y1="15" x2="16" y2="15" />}
      {kind === 'frown' && <path d="M16 16s-1.5-2-4-2-4 2-4 2" />}
    </svg>
  );
}

/** Converts the cut timer's runtime phase + elapsed arrays into the
 *  storage shape persisted on `visits.phase_times` (migration 0075).
 *  Skips phases that are zero-time AND below the current cursor (the
 *  barber moved past them via the Skip button); a zero-time phase
 *  that's the CURRENT phase is just "haven't gotten there yet" and
 *  shouldn't be marked skipped retroactively.
 *
 *  Returns null for single-phase services — there's nothing to break
 *  down. The recap / analytics surfaces treat null and empty as
 *  equivalent ("no per-phase data available"). */
function buildPhaseTimes(
  phases: ReadonlyArray<{ name: string; nameKey?: string }>,
  elapsed: number[],
): VisitPhaseTime[] | null {
  if (phases.length <= 1) return null;
  return phases.map((p, i) => ({
    // Persist English `name` as the stable key; the UI maps via
    // `nameKey` at render time so the stored value isn't locale-bound.
    name: p.name,
    seconds: Math.round(elapsed[i] ?? 0),
    // A phase is "skipped" when it logged zero seconds. Could be either
    // explicit skip or just an in-progress cut, but we record the same
    // way — the analytics layer can distinguish by checking total
    // duration vs sum of seconds across phases.
    skipped: (elapsed[i] ?? 0) === 0,
  }));
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function TrendUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}
function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function LightbulbIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A7 7 0 0 0 12 2z" />
    </svg>
  );
}
function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
